import React, { useState } from 'react';

const HotelFacilities = ({ facilities = null }) => {
  const [showCompleteFacilities, setShowCompleteFacilities] = useState(false);

  // Normalize and dynamically group flat lists of facilities into structured categories
  const normalizeFacilities = (raw) => {
    if (!raw) return {};
    
    let facilitiesList = [];
    if (typeof raw === 'string') {
      // If it's a raw comma-separated string
      facilitiesList = raw.split(',').map(s => s.trim()).filter(Boolean);
    } else if (Array.isArray(raw)) {
      // Flatten any comma-separated strings inside the array
      raw.forEach(item => {
        if (typeof item === 'string') {
          const splitItems = item.split(',').map(s => s.trim()).filter(Boolean);
          facilitiesList.push(...splitItems);
        } else {
          facilitiesList.push(item);
        }
      });
    } else if (typeof raw === 'object') {
      // If it's already categorized as an object, filter numeric/index keys
      const result = {};
      Object.entries(raw).forEach(([key, value]) => {
        if (/^\d+$/.test(key)) {
          const displayKey = `Category ${parseInt(key) + 1}`;
          result[displayKey] = Array.isArray(value) ? value : [value];
        } else {
          result[key] = value;
        }
      });
      return result;
    }

    // Dynamic categorization rules for flat lists of amenities
    const categories = {
      'Internet & Connectivity': [],
      'Food & Drink': [],
      'Wellness & Recreation': [],
      'Transport & Parking': [],
      'Services & Reception': [],
      'Room Comforts': [],
      'Safety & Accessibility': [],
      'General & Others': []
    };

    const rules = [
      { category: 'Internet & Connectivity', keywords: ['wifi', 'internet', 'wlan', 'wi-fi', 'broadband'] },
      { category: 'Food & Drink', keywords: ['dining', 'restaurant', 'bar', 'cafe', 'breakfast', 'food', 'drink', 'coffee', 'snack', 'lounge', 'buffet', 'meal', 'cuisine', 'tea', 'water', 'minibar', 'mini-bar'] },
      { category: 'Wellness & Recreation', keywords: ['pool', 'swimming', 'spa', 'wellness', 'massage', 'gym', 'fitness', 'sauna', 'jacuzzi', 'sport', 'tennis', 'golf', 'beach', 'recreation', 'billiards', 'play', 'game', 'club', 'exercise', 'yoga'] },
      { category: 'Transport & Parking', keywords: ['parking', 'garage', 'car', 'shuttle', 'transfer', 'taxi', 'airport', 'valet', 'bicycle', 'rent', 'pick-up', 'drop-off', 'coach'] },
      { category: 'Services & Reception', keywords: ['reception', 'front desk', 'check-in', 'check-out', 'concierge', 'luggage', 'baggage', 'currency', 'exchange', 'atm', 'laundry', 'cleaning', 'dry cleaning', 'multilingual', 'porter', 'bellhop', 'room service', 'housekeeping', 'maid'] },
      { category: 'Room Comforts', keywords: ['air conditioning', 'ac', 'heating', 'safe', 'balcony', 'terrace', 'tv', 'desk', 'toiletries', 'bathroom', 'shower', 'tub', 'hairdryer', 'bedding', 'kitchen', 'fridge', 'telephone', 'wardrobe', 'fan', 'rollaway'] },
      { category: 'Safety & Accessibility', keywords: ['accessible', 'wheelchair', 'elevator', 'lift', 'security', 'cctv', 'fire', 'smoke', 'alarm', 'lock', 'guard', 'doctor', 'medical', 'first aid'] }
    ];

    facilitiesList.forEach(item => {
      const itemStr = (typeof item === 'object' && item.name) ? item.name : String(item);
      const lower = itemStr.toLowerCase();
      let matched = false;

      for (const rule of rules) {
        if (rule.keywords.some(keyword => lower.includes(keyword))) {
          categories[rule.category].push(item);
          matched = true;
          break;
        }
      }

      if (!matched) {
        categories['General & Others'].push(item);
      }
    });

    // Remove empty categories
    const filteredResult = {};
    Object.entries(categories).forEach(([key, items]) => {
      if (items.length > 0) {
        filteredResult[key] = items;
      }
    });

    return filteredResult;
  };

  const normalizedFacilities = normalizeFacilities(facilities);

  // If no facilities data is provided, show a placeholder
  if (!normalizedFacilities || Object.keys(normalizedFacilities).length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Hotel Amenities & Facilities</h2>
          <p className="text-gray-600">Facility information will be displayed when available from the hotel.</p>
        </div>
      </div>
    );
  }

  const getIcon = (item) => {
    const iconMap = {
      'wifi': '📶',
      'internet': '📶',
      'air conditioning': '❄️',
      'elevator': '🛗',
      'housekeeping': '🧹',
      'food': '🍽️',
      'delivery': '🍽️',
      'cleaning': '👔',
      'balcony': '🏞️',
      'terrace': '🏞️',
      'accessible': '♿',
      'baby': '👶',
      'fireplace': '🔥',
      'gift': '🎁',
      'shop': '🎁',
      'parking': '🚗',
      'shuttle': '✈️',
      'taxi': '🚕',
      'bicycle': '🚲',
      'security': '🔒',
      'cctv': '📹',
      'key': '🔑',
      'fire': '🚨',
      'first aid': '🏥',
      'laundry': '👔',
      'currency': '💱',
      'atm': '🏧',
      'luggage': '📦'
    };

    const itemLower = item.toLowerCase();
    for (const [key, icon] of Object.entries(iconMap)) {
      if (itemLower.includes(key)) {
        return icon;
      }
    }
    return '✓';
  };

  const getCategoryIcon = (category, colorClass) => {
    const icons = {
      'Internet & Connectivity': (
        <svg className={`w-5 h-5 ${colorClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01M5.283 13.576a9 9 0 0113.434 0M2.455 10.749a12.5 12.5 0 0119.09 0" />
        </svg>
      ),
      'Food & Drink': (
        <svg className={`w-5 h-5 ${colorClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      'Wellness & Recreation': (
        <svg className={`w-5 h-5 ${colorClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      'Transport & Parking': (
        <svg className={`w-5 h-5 ${colorClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      ),
      'Services & Reception': (
        <svg className={`w-5 h-5 ${colorClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
        </svg>
      ),
      'Room Comforts': (
        <svg className={`w-5 h-5 ${colorClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      'Safety & Accessibility': (
        <svg className={`w-5 h-5 ${colorClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    };

    return icons[category] || (
      <svg className={`w-5 h-5 ${colorClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
      </svg>
    );
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Amenities and Facilities</h2>
        {Object.keys(normalizedFacilities).length > 0 && (
          <button
            onClick={() => setShowCompleteFacilities(!showCompleteFacilities)}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors"
          >
            {showCompleteFacilities ? (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
                Show Less
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                View All Facilities
              </>
            )}
          </button>
        )}
      </div>
      
      {/* Basic Facilities View */}
      {!showCompleteFacilities && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(normalizedFacilities).map(([category, items], index) => {
            if (!items || (Array.isArray(items) && items.length === 0)) return null;
            
            const categoryColors = [
              'bg-blue-50 border-blue-100',
              'bg-green-50 border-green-100', 
              'bg-orange-50 border-orange-100',
              'bg-purple-50 border-purple-100',
              'bg-pink-50 border-pink-100',
              'bg-indigo-50 border-indigo-100',
              'bg-teal-50 border-teal-100',
              'bg-gray-50 border-gray-100'
            ];
            
            const iconColors = [
              'text-blue-500',
              'text-green-500',
              'text-orange-500', 
              'text-purple-500',
              'text-pink-500',
              'text-indigo-500',
              'text-teal-500',
              'text-gray-500'
            ];

            return (
              <div key={category} className={`${categoryColors[index % categoryColors.length]} rounded-xl p-4 border`}>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center capitalize">
                  {getCategoryIcon(category, `${iconColors[index % iconColors.length]} mr-2`)}
                  {category}
                </h3>
                <ul className="space-y-2 text-sm">
                  {Array.isArray(items) ? (
                    items.slice(0, 3).map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-center text-gray-700">
                        <span className={`${iconColors[index % iconColors.length]} mr-2`}>
                          {typeof item === 'object' && item.name ? getIcon(item.name) : getIcon(item)}
                        </span>
                        {typeof item === 'object' && item.name ? item.name : item}
                      </li>
                    ))
                  ) : (
                    <li className="flex items-center text-gray-700">
                      <span className={`${iconColors[index % iconColors.length]} mr-2`}>
                        {getIcon(items)}
                      </span>
                      {items}
                    </li>
                  )}
                  {Array.isArray(items) && items.length > 3 && (
                    <li className="text-xs text-gray-500 italic ml-6">
                      +{items.length - 3} more items
                    </li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/* Complete Facilities View */}
      {showCompleteFacilities && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Object.entries(normalizedFacilities).map(([category, items], index) => {
              if (!items || (Array.isArray(items) && items.length === 0)) return null;

              const categoryColors = [
                'bg-blue-50 border-blue-100',
                'bg-green-50 border-green-100', 
                'bg-orange-50 border-orange-100',
                'bg-purple-50 border-purple-100',
                'bg-pink-50 border-pink-100',
                'bg-indigo-50 border-indigo-100',
                'bg-teal-50 border-teal-100',
                'bg-gray-50 border-gray-100'
              ];
              
              const iconColors = [
                'text-blue-500',
                'text-green-500',
                'text-orange-500', 
                'text-purple-500',
                'text-pink-500',
                'text-indigo-500',
                'text-teal-500',
                'text-gray-500'
              ];

              return (
                <div key={category} className={`${categoryColors[index % categoryColors.length]} rounded-xl p-6 border`}>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center capitalize">
                    {getCategoryIcon(category, `${iconColors[index % iconColors.length]} mr-2`)}
                    {category}
                  </h3>
                  <ul className="space-y-2">
                    {Array.isArray(items) ? (
                      items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start text-gray-700">
                          <span className={`${iconColors[index % iconColors.length]} mr-2 mt-0.5`}>
                            {typeof item === 'object' && item.name ? getIcon(item.name) : getIcon(item)}
                          </span>
                          <span className="text-sm">
                            {typeof item === 'object' && item.name ? item.name : item}
                          </span>
                        </li>
                      ))
                    ) : (
                      <li className="flex items-start text-gray-700">
                        <span className={`${iconColors[index % iconColors.length]} mr-2 mt-0.5`}>
                          {getIcon(items)}
                        </span>
                        <span className="text-sm">{items}</span>
                      </li>
                    )}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelFacilities;